import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Briefcase, MapPin, Award, Save, Loader2, Camera, X, Check } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { Navigation } from '../../components/Navigation';

const EXPERIENCE_LEVELS = [
  { value: 'entry', label: 'Entry Level', description: '0-2 years' },
  { value: 'mid', label: 'Mid Level', description: '3-5 years' },
  { value: 'senior', label: 'Senior Level', description: '6-10 years' },
  { value: 'executive', label: 'Executive', description: '10+ years' },
];

const JOB_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
];

const COMMON_SKILLS = [
  'JavaScript', 'Python', 'React', 'TypeScript', 'Node.js', 'SQL',
  'Java', 'AWS', 'Docker', 'Git', 'REST APIs', 'Agile',
  'Project Management', 'Communication', 'Leadership', 'Analytics',
  'Marketing', 'Sales', 'Customer Service', 'Design',
];

export function Profile() {
  const { user, profile } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [customSkill, setCustomSkill] = useState('');

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    location: '',
    avatar_url: '',
    job_title: '',
    experience_level: '',
    min_salary: '',
    max_salary: '',
    skills: [] as string[],
    desired_job_types: [] as string[],
    remote_preference: true,
    bio: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        email: profile.email || '',
        phone: (profile as any).phone || '',
        location: profile.location || '',
        avatar_url: profile.avatar_url || '',
        job_title: (profile as any).job_title || '',
        experience_level: (profile as any).experience_level || '',
        min_salary: (profile as any).min_salary?.toString() || '',
        max_salary: (profile as any).max_salary?.toString() || '',
        skills: (profile as any).skills || [],
        desired_job_types: (profile as any).desired_job_types || [],
        remote_preference: (profile as any).remote_preference ?? true,
        bio: profile.bio || '',
      });
    }
  }, [user, profile, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const updates: any = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || null,
        location: formData.location || null,
        avatar_url: formData.avatar_url || null,
        job_title: formData.job_title || null,
        experience_level: formData.experience_level || null,
        min_salary: formData.min_salary ? parseInt(formData.min_salary) : null,
        max_salary: formData.max_salary ? parseInt(formData.max_salary) : null,
        skills: formData.skills.length > 0 ? formData.skills : null,
        desired_job_types: formData.desired_job_types.length > 0 ? formData.desired_job_types : null,
        remote_preference: formData.remote_preference,
        bio: formData.bio || null,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user!.id);

      if (updateError) {
        console.error('Profile update error:', updateError);
        setError('Failed to update profile. Please try again.');
        setLoading(false);
        return;
      }

      setSuccess(true);
      setLoading(false);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const toggleSkill = (skill: string) => {
    if (formData.skills.includes(skill)) {
      setFormData({
        ...formData,
        skills: formData.skills.filter((s) => s !== skill),
      });
    } else {
      setFormData({
        ...formData,
        skills: [...formData.skills, skill],
      });
    }
  };

  const addCustomSkill = () => {
    const skill = customSkill.trim();
    if (skill && !formData.skills.includes(skill)) {
      setFormData({
        ...formData,
        skills: [...formData.skills, skill],
      });
      setCustomSkill('');
    }
  };

  const removeSkill = (skill: string) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((s) => s !== skill),
    });
  };

  const toggleJobType = (type: string) => {
    if (formData.desired_job_types.includes(type)) {
      setFormData({
        ...formData,
        desired_job_types: formData.desired_job_types.filter((t) => t !== type),
      });
    } else {
      setFormData({
        ...formData,
        desired_job_types: [...formData.desired_job_types, type],
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">My Profile</h1>
          <p className="text-gray-600">Manage your personal information and job preferences</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-3">
            <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700 font-medium">Profile updated successfully!</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
            </div>

            <div className="space-y-6">
              {/* Profile Photo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Profile Photo
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {formData.avatar_url ? (
                      <img src={formData.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition"
                    >
                      <Camera className="w-4 h-4" />
                      Upload Photo
                    </button>
                    <p className="text-xs text-gray-500 mt-2">JPG, PNG or GIF. Max 2MB.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="Jane Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="jane@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="San Francisco, CA"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="bio" className="block text-sm font-semibold text-gray-700 mb-2">
                  About Me
                </label>
                <textarea
                  id="bio"
                  rows={4}
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition resize-none"
                  placeholder="Tell us about yourself, your career goals, and what you're looking for..."
                />
              </div>
            </div>
          </div>

          {/* Career Information */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <Briefcase className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-gray-900">Career Information</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="job_title" className="block text-sm font-semibold text-gray-700 mb-2">
                  Desired Job Title
                </label>
                <input
                  type="text"
                  id="job_title"
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                  placeholder="e.g., Software Engineer, Marketing Manager"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Experience Level
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {EXPERIENCE_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, experience_level: level.value })}
                      className={`p-4 border-2 rounded-xl text-left transition ${
                        formData.experience_level === level.value
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-semibold text-gray-900 text-sm">{level.label}</div>
                      <div className="text-xs text-gray-600">{level.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="min_salary" className="block text-sm font-semibold text-gray-700 mb-2">
                    Minimum Salary
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      id="min_salary"
                      value={formData.min_salary}
                      onChange={(e) => setFormData({ ...formData, min_salary: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                      placeholder="50000"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="max_salary" className="block text-sm font-semibold text-gray-700 mb-2">
                    Maximum Salary
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      id="max_salary"
                      value={formData.max_salary}
                      onChange={(e) => setFormData({ ...formData, max_salary: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                      placeholder="80000"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-gray-900">Skills</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select Skills
                </label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {COMMON_SKILLS.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => toggleSkill(skill)}
                      className={`px-4 py-2 rounded-full font-medium text-sm transition ${
                        formData.skills.includes(skill)
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="custom_skill" className="block text-sm font-semibold text-gray-700 mb-2">
                  Add Custom Skill
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="custom_skill"
                    value={customSkill}
                    onChange={(e) => setCustomSkill(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomSkill();
                      }
                    }}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl bg-white text-gray-900 focus:border-orange-500 focus:outline-none transition"
                    placeholder="Type a skill and press Enter"
                  />
                  <button
                    type="button"
                    onClick={addCustomSkill}
                    className="px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition"
                  >
                    Add
                  </button>
                </div>
              </div>

              {formData.skills.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Skills ({formData.skills.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <span
                        key={skill}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm font-medium"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill(skill)}
                          className="hover:text-orange-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Work Preferences */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-gray-900">Work Preferences</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-xl cursor-pointer hover:border-orange-300 transition">
                  <input
                    type="checkbox"
                    checked={formData.remote_preference}
                    onChange={(e) => setFormData({ ...formData, remote_preference: e.target.checked })}
                    className="w-5 h-5 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">Open to remote work</div>
                    <div className="text-sm text-gray-600">Show me remote job opportunities</div>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Desired Job Types
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {JOB_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => toggleJobType(type.value)}
                      className={`p-3 border-2 rounded-xl font-medium transition ${
                        formData.desired_job_types.includes(type.value)
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Profile
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
